import type { CanvasAgentNode } from '../canvas-view/canvas-view-model';

/**
 * The Design page's agent list (Mostafa's "every agent on the Design page",
 * 2026-09-24): every agent in the solution as a pill, two lines at most, the
 * rest behind "See N more". Pure so the ordering and the fitting are tested
 * without a DOM; the component only measures.
 */

/** Which logo a core pill carries; use-case agents carry none. */
export type CoreAgentKind = 'supervisor' | 'knowledge';

export const coreKindFor = (agent: CanvasAgentNode): CoreAgentKind | null => {
  if (agent.category !== 'core') return null;
  return agent.title.trim().toLowerCase().startsWith('knowledge')
    ? 'knowledge'
    : 'supervisor';
};

const CORE_ORDER: Record<CoreAgentKind, number> = {
  supervisor: 0,
  knowledge: 1,
};

/** Supervisor, then Knowledge, then every use-case agent in row order. */
export const orderForOverview = (
  agents: CanvasAgentNode[]
): CanvasAgentNode[] => {
  const core = agents
    .filter((a) => a.category === 'core')
    .sort(
      (a, b) =>
        CORE_ORDER[coreKindFor(a) ?? 'supervisor'] -
        CORE_ORDER[coreKindFor(b) ?? 'supervisor']
    );
  return [...core, ...agents.filter((a) => a.category !== 'core')];
};

export interface AgentSplit {
  core: CanvasAgentNode[];
  useCase: CanvasAgentNode[];
  totalNodes: number;
}

export const splitAgents = (agents: CanvasAgentNode[]): AgentSplit => {
  const ordered = orderForOverview(agents);
  return {
    core: ordered.filter((a) => a.category === 'core'),
    useCase: ordered.filter((a) => a.category !== 'core'),
    totalNodes: agents.reduce((sum, a) => sum + a.nodeCount, 0),
  };
};

export interface FitInput {
  /** Each pill's rendered width, in list order. */
  widths: number[];
  containerWidth: number;
  gap: number;
  /** The "See N more" button's width. */
  moreWidth: number;
  maxLines?: number;
}

/** How many lines `widths` wrap onto, flex-wrap style. */
const linesFor = (widths: number[], containerWidth: number, gap: number) => {
  let lines = 0;
  let x = 0;
  widths.forEach((w) => {
    if (lines === 0 || (x > 0 && x + gap + w > containerWidth)) {
      lines += 1;
      x = w;
    } else {
      x += gap + w;
    }
  });
  return lines;
};

/**
 * How many pills to show so they, plus the "See N more" button when any are
 * left over, stay within `maxLines`. All of them when they already fit.
 */
export const fitPillCount = ({
  widths,
  containerWidth,
  gap,
  moreWidth,
  maxLines = 2,
}: FitInput): number => {
  if (containerWidth <= 0) return widths.length;
  if (linesFor(widths, containerWidth, gap) <= maxLines) return widths.length;
  for (let shown = widths.length - 1; shown > 0; shown -= 1) {
    const withMore = [...widths.slice(0, shown), moreWidth];
    if (linesFor(withMore, containerWidth, gap) <= maxLines) return shown;
  }
  return 0;
};
